import { useState } from 'react';
import { DateTime } from 'luxon';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import {
  CallFollowUpType,
  CallFollowUpTypeLabels,
  currencyFormatter,
  ICallRecordedFollowUp,
  ICallTranscriptSegment,
  InvoiceQueryReasonLabels,
} from 'shared';

import { Badge } from '../../../common/Atoms/Badge';
import { classNames } from '../../../common/lib/classNames';
import { getFrequencyTypeLabel } from '../../../common/Modals/CreatePaymentPlanModal';
import { useFetchCallFollowUpsQuery, useFetchCallTranscriptQuery } from '../../services/api/contactsApi/contact';
import { WrappedLink } from '../WrappedLink';

export const followUpBadgeColor: Record<CallFollowUpType, `blue` | `orange` | `purple` | `indigo`> = {
  [CallFollowUpType.PAYMENT_PROMISE]: `blue`,
  [CallFollowUpType.INVOICE_QUERY]: `orange`,
  [CallFollowUpType.PAYMENT_PLAN]: `purple`,
  [CallFollowUpType.FOLLOW_UP_EMAIL]: `indigo`,
};

/** e.g. "A$914.55 by 7 Oct 2026, open", "Not received, resolved", "4 weekly payments", "Following up on our call, sent" */
export function describeRecordedFollowUp(item: ICallRecordedFollowUp) {
  const parts: string[] = [];

  if (item.type === CallFollowUpType.FOLLOW_UP_EMAIL) {
    if (item.emailSubject) parts.push(item.emailSubject);
  }
  else if (item.type === CallFollowUpType.PAYMENT_PROMISE) {
    if (item.promisedAmountCents !== null) parts.push(currencyFormatter(item.currency || undefined)(item.promisedAmountCents / 100));
    if (item.promisedDate) parts.push(`by ${DateTime.fromISO(item.promisedDate).toFormat(`d LLL yyyy`)}`);
  }
  else if (item.type === CallFollowUpType.INVOICE_QUERY) {
    if (item.queryReason) parts.push(InvoiceQueryReasonLabels[item.queryReason]);
  }
  else if (item.planPayments || item.planFrequency) {
    const frequency = item.planFrequency ? getFrequencyTypeLabel(item.planFrequency).toLowerCase() : null;

    parts.push([item.planPayments, frequency, `payments`].filter(Boolean).join(` `));
  }

  const detail = parts.join(` `);

  return item.status ? `${detail}${detail ? `, ` : ``}${item.status}` : detail;
}

/** What was recorded from a call, one line each with a link to the invoice when there is one */
export function RecordedFollowUps({ items, className }: { items: ICallRecordedFollowUp[]; className?: string }) {
  return (
    <ul className={ classNames(`space-y-1.5`, className) }>
      { items.map((item, i) => (
        <li
          key={ `${item.type}-${item.invoiceId || i}` }
          className={ `flex flex-wrap items-center gap-x-2 gap-y-1 text-sm` }
        >
          <Badge color={ followUpBadgeColor[item.type] }>{ CallFollowUpTypeLabels[item.type] }</Badge>
          { item.invoiceId ? (
            <WrappedLink
              to={ `/invoices/${item.invoiceId}` }
              className={ `font-medium text-paidniceGreen-600 hover:text-paidniceGreen-700 hover:underline` }
            >
              { item.invoiceNumber || `Invoice` }
            </WrappedLink>
          ) : null }
          <span className={ `text-gray-600` }>{ describeRecordedFollowUp(item) }</span>
        </li>
      )) }
    </ul>
  );
}

export function formatDuration(seconds: number | null) {
  const total = Math.max(0, seconds || 0);
  const mins = Math.floor(total / 60);
  const secs = total % 60;

  return `${mins}:${String(secs).padStart(2, `0`)}`;
}

interface ITranscriptBubblesProps {
  segments: ICallTranscriptSegment[];
  userLabel: string;
  contactLabel: string;
}

/** Chat style transcript: the Paidnice user on the right, the contact on the left */
export function TranscriptBubbles({ segments, userLabel, contactLabel }: ITranscriptBubblesProps) {
  return (
    <div className={ `space-y-2` }>
      { segments.map((segment, i) => (
        <div
          key={ i }
          className={ classNames(`flex`, segment.speaker === `user` ? `justify-end` : `justify-start`) }
        >
          <div className={ classNames(
            `max-w-[80%] whitespace-normal rounded-2xl px-3 py-2 text-sm`,
            segment.speaker === `user` ? `rounded-br-sm bg-paidniceGreen-600 text-white` : `rounded-bl-sm border border-gray-200 bg-white text-gray-800`,
          ) }>
            <span className={ classNames(`block text-[0.65rem] font-semibold uppercase tracking-wide`, segment.speaker === `user` ? `text-paidniceGreen-100` : `text-gray-400`) }>
              { segment.speaker === `user` ? userLabel : contactLabel }
              { ` · ${formatDuration(Math.floor(segment.start))}` }
            </span>
            { segment.text }
          </div>
        </div>
      )) }
    </div>
  );
}

interface ICallTranscriptProps {
  organisationId: string;
  contactId: number;
  contactCallId: string;
  /** Who placed the call, for the right hand bubbles */
  callerName?: string;
  /** Leave out what was recorded from the call, e.g. on the invoice that already shows it */
  hideFollowUps?: boolean;
}

/**
 * "View transcript" toggle under a logged call, with links to anything recorded from it. Fetches the transcript from
 * the call row the first time it opens.
 * ponytail: one follow ups request per call in view. Fold it into the activity and note responses if feeds get long
 */
export function CallTranscript({ organisationId, contactId, contactCallId, callerName, hideFollowUps }: ICallTranscriptProps) {
  const [open, setOpen] = useState(false);
  const { data, isFetching, isError } = useFetchCallTranscriptQuery(
    { organisationId, contactId, callId: contactCallId },
    { skip: !open },
  );
  const { data: followUps } = useFetchCallFollowUpsQuery(
    { organisationId, contactId, callId: contactCallId },
    { skip: hideFollowUps },
  );

  return (
    <div className={ `mt-2` }>
      { !hideFollowUps && followUps?.length ? (
        <RecordedFollowUps
          items={ followUps }
          className={ `mb-2` }
        />
      ) : null }
      <button
        type={ `button` }
        onClick={ () => setOpen(!open) }
        aria-expanded={ open }
        className={ `flex cursor-pointer items-center gap-1 text-xs font-medium text-paidniceGreen-700 hover:text-paidniceGreen-900` }
      >
        { open ? `Hide transcript` : `View transcript` }
        <ChevronDownIcon className={ classNames(`h-3.5 w-3.5 transition-transform`, open && `rotate-180`) } />
      </button>
      { open ? (
        <div className={ `mt-2 max-h-96 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3` }>
          { data?.transcript?.length ? (
            <TranscriptBubbles
              segments={ data.transcript }
              userLabel={ callerName || `Caller` }
              contactLabel={ `Contact` }
            />
          ) : (
            <p className={ `text-xs text-gray-500` }>
              { isFetching ? `Loading transcript...` : isError ? `Could not load the transcript.` : `No transcript for this call.` }
            </p>
          ) }
        </div>
      ) : null }
    </div>
  );
}

/** "From a call" with its transcript, for a promise, query or plan recorded from click to call follow ups */
export function FromCall({ organisationId, contactId, contactCallId }: Pick<ICallTranscriptProps, `organisationId` | `contactId` | `contactCallId`>) {
  return (
    <span className={ `block text-xs text-gray-500` }>
      { `From a call` }
      <CallTranscript
        organisationId={ organisationId }
        contactId={ contactId }
        contactCallId={ contactCallId }
        hideFollowUps
      />
    </span>
  );
}
